import type { ID } from "@mb-pro-ui/utils";

// Only the attributes this app requests. Every type is a sparse view of the
// backend model: a field missing from the response (not requested, or
// removed by the operator's field blacklist) is undefined, never null.

export type Maybe<T> = T | null;

export interface AllowedOrgEntry {
  "org-id"?: number;
  "org-name": string | null;
  "assumed-local-user": string;
  "expires-at": string | null;
  "is-home": boolean;
}

export interface Org {
  type: "admin/orgs" | "orgs";
  id: ID;
  name: Maybe<string>;
}

export interface Operator {
  type: "alarm/operators" | "operators";
  id: ID;
  admin?: boolean;
  "customer-manager-right"?: boolean;
  "field-blacklists"?: Maybe<Record<string, Record<string, boolean>>>;
}

export interface User {
  type: "admin/users" | "users";
  id: ID;
  name?: string;
  username?: string;
  admin?: boolean;
  "ui-language"?: Maybe<string>;
  org?: Org | ID;
  "alarm-operator"?: Maybe<Operator | ID>;
  "allowed-orgs"?: Maybe<AllowedOrgEntry[]>;
}

export interface LookupWithDescription {
  type: string;
  id: ID;
  description?: Maybe<string>;
  "localized-description"?: Maybe<string>;
}

export interface CustomerManagerProfile {
  type: "alarm/customer-manager-profiles" | "customer-manager-profiles";
  id: ID;
  settings?: Maybe<{ menu?: Record<string, number> }>;
}

export interface Customer {
  type: "alarm/customers" | "customers";
  id: ID;
  account?: string;
  name?: Maybe<string>;
  address?: Maybe<string>;
  passwd?: Maybe<string>;
  active?: boolean;
  "temp-inactive-ts"?: Maybe<string>;
  "last-event-ts"?: Maybe<string>;
  group?: Maybe<LookupWithDescription | ID>;
  "object-type"?: Maybe<LookupWithDescription | ID>;
  "customer-manager-profile"?: Maybe<CustomerManagerProfile | ID>;
  zones?: ID[];
  partitions?: ID[];
  users?: ID[];
  "notifiable-persons"?: ID[];
}

export interface Zone {
  type: "alarm/zones" | "zones";
  id: ID;
  number?: Maybe<string>;
  place?: Maybe<string>;
  "temporary-inactive-ts"?: Maybe<string>;
  "zone-type"?: Maybe<LookupWithDescription | ID>;
  "sensor-type"?: Maybe<LookupWithDescription | ID>;
}

export interface Partition {
  type: "alarm/partitions" | "partitions";
  id: ID;
  number?: Maybe<string>;
  description?: Maybe<string>;
}

export interface CustomerUser {
  type: "alarm/users" | "users";
  id: ID;
  number?: Maybe<string>;
  name?: Maybe<string>;
  role?: Maybe<string>;
  phone?: Maybe<string>;
}

export interface NotifiablePerson {
  type: "alarm/notifiable-persons" | "notifiable-persons";
  id: ID;
  idx?: Maybe<number>;
  name?: Maybe<string>;
  role?: Maybe<string>;
  phone?: Maybe<string>;
  mobile?: Maybe<string>;
  email?: Maybe<string>;
  remark?: Maybe<string>;
}

export interface Cdec {
  type: "alarm/cdec-all" | "cdec-all";
  id: ID;
  active?: boolean;
  arrived?: string;
  sent?: Maybe<string>;
  color?: Maybe<string>;
  "event-code"?: Maybe<string>;
  "localized-description"?: Maybe<string>;
  "zone-number"?: Maybe<string>;
  "partition-number"?: Maybe<string>;
  zone?: Maybe<Pick<Zone, "type" | "id" | "place"> | ID>;
  user?: Maybe<Pick<CustomerUser, "type" | "id" | "name"> | ID>;
}

export interface EntitySearchCustomer {
  id: ID;
  account: string;
  name: Maybe<string>;
  address?: Maybe<string>;
  active?: boolean;
  "temp-inactive-ts"?: Maybe<string>;
}

export interface EntitySearchResponse {
  query: string;
  results: { customers?: EntitySearchCustomer[] };
  excluded: string[];
}

export function related<T extends { id: ID }>(value: T | ID | null | undefined): T | undefined {
  return value && typeof value === "object" ? value : undefined;
}
